import React, { useState, useCallback, useEffect } from 'react';
import { Button, CardHeader } from 'reactstrap';
import ButtonLoader from '../../../../../components/Layout/Buttons/ButtonLoader';
import { LegalIssuesForm } from '../../../../../components/LoiForm/LegalIssuesForm';
import { LoiForm } from '../../../../../slices/marketplace/loi-form/loi-form';
import { LoiFormMode } from '../../../../../slices/marketplace/loi-form/loi-form-mode.enum';
import { updateLoiForm } from '../../../../../slices/marketplace/loi-form/loi-forms-api';
import { GetBuyer } from '../../../../../slices/marketplace/buyer/thunks/get-buyer.thunk';
import { useStateSelector } from '../../../../../store/selectors';
import createNotification from '../../../../../utils/createNotification';
import { useDispatch } from 'react-redux';
import Helpers from '../../../../../utils/helper';
import './LegalIssuesTab.scss';

export interface LegalIssuesTabProps {
    setIsEditMode?: (isEditMode: boolean) => void;
}

export const LegalIssuesTab = (props: LegalIssuesTabProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const targetBuyer = useStateSelector(
        (state) => state.buyersState.targetBuyer
    );
    const profile = targetBuyer?.profile;
    const dispatch = useDispatch() as any;
    const loiForm = profile?.loiForm;
    const loiFormId = profile?.loiFormId;

    const [formData, setFormData] = useState<LoiForm | null>(loiForm ?? null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setFormData(loiForm ?? null);
    }, [loiForm]);

    const handleChange = useCallback((payload: LoiForm) => {
        setFormData((prev) =>
            prev
                ? {
                      ...prev,
                      legalIssueSections: payload.legalIssueSections,
                      legalIssues: payload.legalIssues,
                  }
                : null
        );
    }, []);

    const handleSave = useCallback(async () => {
        if (!axios || !loiFormId || !formData) return;
        setIsSaving(true);
        try {
            const loiPayload: LoiForm = {
                ...formData,
                id: loiFormId,
                isAdminTemplate: false,
                termForm: formData.termForm,
                additionalCovenants: formData.additionalCovenants ?? [],
            };
            await updateLoiForm(axios, loiFormId, loiPayload);
            await dispatch(GetBuyer({ buyerId: null }));
        } catch {
            createNotification(
                'Could not save. Please try again.',
                'error'
            );
        } finally {
            setIsSaving(false);
        }
    }, [axios, loiFormId, formData, dispatch]);

    if (!profile) {
        return (
            <div className="legal-issues-tab">
                {Helpers.renderTableLoader()}
            </div>
        );
    }

    if (!loiForm && !formData) {
        return (
            <div className="legal-issues-tab">
                <div className="legal-issues-tab-empty text-muted">
                    Legal issues form is not available. Please refresh the page
                    or contact support.
                </div>
            </div>
        );
    }

    const data = formData ?? loiForm;
    if (!data) {
        return null;
    }

    return (
        <div className="legal-issues-tab">
            <CardHeader className="legal-issues-tab-header space-between">
                Legal Issues
                <div className="buttons-wrapper">
                    <ButtonLoader
                        buttonText="Save"
                        loaderButtonText=""
                        disabled={false}
                        isLoading={isSaving}
                        onClick={handleSave}
                        className="btn event-btn btn-primary upload-btn loader-submit"
                    />
                    <Button
                        className="btn btn-primary"
                        onClick={() => props.setIsEditMode?.(false)}>
                        Back
                    </Button>
                </div>
            </CardHeader>
            <div className="legal-issues-tab-body">
                <LegalIssuesForm
                    legalIssueSections={data.legalIssueSections}
                    legalIssues={data.legalIssues}
                    termForm={data.termForm}
                    onChange={handleChange}
                    mode={LoiFormMode.BuyerProfile}
                />
            </div>
        </div>
    );
};
